import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Calendar, User, Clock, CheckCircle, XCircle, Ban,
  FileText, MessageSquare, AlertTriangle, Shield, Pencil
} from 'lucide-react';
import api from '../utils/axios';
import remountOnId from '../utils/remountOnId';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { getStatusBadgeClasses, formatStatusText } from '../utils/statusUtils';
import { formatDateOnly } from '../utils/dateUtils';
import LeaveApprovalTrail from '../components/LeaveApprovalTrail';
import useLatestRequest from '../hooks/useLatestRequest';
import CModal from '../components/shared/c-modal';

const LeaveDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, hasPermission, hasAnyResourceAction } = useAuth();

  const [request, setRequest] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Action modals state
  const [showApprove, setShowApprove] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [approveComments, setApproveComments] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [rejectComments, setRejectComments] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  const [acting, setActing] = useState(false);

  const canApprove = hasPermission('leaves', 'approve');

  // Only the latest load may populate the page: StrictMode runs the mount effect twice
  // and every action re-fetches, so a late response would otherwise overwrite the
  // internal notes the reviewer has typed since.
  const beginRequest = useLatestRequest();
  const fetchData = useCallback(async () => {
    const isCurrent = beginRequest();
    try {
      setLoading(true);
      const res = await api.get(`/api/leaves/${id}`);
      if (!isCurrent()) return;
      setRequest(res.data.data);
      setAuditLogs(res.data.audit_logs || []);
      setInternalNotes(res.data.data?.hr_internal_notes || '');
    } catch (err) {
      if (!isCurrent()) return;
      toast.error(err.response?.data?.message || 'Failed to load leave request');
      navigate('/leaves');
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [id, navigate, beginRequest]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleApprove = async () => {
    setActing(true);
    try {
      const res = await api.put(`/api/leaves/${id}/approve`, { review_comments: approveComments });
      // A chain can leave the request pending for its next approver; the server says which.
      toast.success(res.data?.message || 'Leave request approved');
      setShowApprove(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to approve request');
    } finally {
      setActing(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) { toast.error('Rejection reason is required'); return; }
    setActing(true);
    try {
      await api.put(`/api/leaves/${id}/reject`, {
        rejection_reason: rejectReason,
        review_comments: rejectComments,
      });
      toast.success('Leave request rejected');
      setShowReject(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to reject request');
    } finally {
      setActing(false);
    }
  };

  const handleCancel = async () => {
    const confirmText = request.status === 'approved'
      ? 'Cancel this APPROVED leave? The days go back onto the balance and the cancellation is recorded in the audit trail.'
      : 'Are you sure you want to cancel this leave request?';
    if (!window.confirm(confirmText)) return;
    setActing(true);
    try {
      await api.put(`/api/leaves/${id}/cancel`);
      toast.success('Leave request cancelled');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to cancel request');
    } finally {
      setActing(false);
    }
  };

  const handleSaveNotes = async () => {
    setActing(true);
    try {
      await api.put(`/api/leaves/${id}/notes`, { hr_internal_notes: internalNotes });
      toast.success('Notes saved');
      setShowNotes(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save notes');
    } finally {
      setActing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (!request) return null;

  const isPending = request.status === 'pending';
  const isApproved = request.status === 'approved';
  const isOwner = user?.employee_id != null && String(user.employee_id) === String(request.employee_id);
  // An approved leave the employee no longer needs can only come back off the books by
  // being cancelled — there is no unapprove. The owner may drop their own request at
  // either stage; an approver only ever cancels an APPROVED one, because turning down a
  // pending request is Reject, which records a reason. Balance restore is server-side.
  const canCancel = isOwner ? (isPending || isApproved) : (canApprove && isApproved);
  // The server decides who may approve or reject THIS request: the chain's current step
  // can name someone without leaves:approve (the requester's line manager), and can rule
  // out someone with it (HR, while the request waits on the line manager).
  const canDecide = isPending && Boolean(request.approval?.can_decide);
  const requesterName = `${request.first_name || ''} ${request.last_name || ''}`.trim();
  const requesterRole = [request.position, request.department_name].filter(Boolean).join(' · ');
  // The profile link only where GET /api/employees/:id will answer: an employees scope of
  // 'all', or 'department' over the requester's own department.
  const canOpenRequester = hasAnyResourceAction('employees', ['read_all', 'update_all', 'delete'])
    || (hasAnyResourceAction('employees', ['read_department', 'update_department'])
      && user?.department_id != null
      && String(user.department_id) === String(request.department_id));
  // A pending request can now change after submission, so "Submitted" alone is misleading.
  // This reads has_edits (an EXISTS over the `updated` audit rows), NOT updated_at -
  // approve, reject, cancel and the internal-notes save all bump updated_at, so a
  // timestamp comparison labels almost every non-pending request as edited.
  const wasEdited = Boolean(request.has_edits);

  const statusIcon = {
    pending: <Clock className="h-5 w-5 text-warning-600" />,
    approved: <CheckCircle className="h-5 w-5 text-success-600" />,
    rejected: <XCircle className="h-5 w-5 text-danger-600" />,
    cancelled: <Ban className="h-5 w-5 text-gray-500" />,
  }[request.status];

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/leaves"
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Link>
          <div className="flex items-center space-x-3">
            <Calendar className="h-6 w-6 text-primary-600" />
            <h1 className="text-2xl font-bold text-gray-900">Leave Request</h1>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {statusIcon}
          <span data-testid="leave-detail-status" className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClasses(request.status, 'leave')}`}>
            {formatStatusText(request.status)}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main */}
        <div className="lg:col-span-2 space-y-6">
          {/* Employee + Request Info */}
          <div className="bg-white rounded-lg shadow-soft border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-base font-semibold text-gray-900 flex items-center">
                <User className="h-4 w-4 mr-2 text-primary-600" />
                Request Details
              </h2>
            </div>
            <div className="px-6 py-4 space-y-4">
              {/* Whose request this is. Shown to everyone but the requester - a lead
                  deciding through the org chart may hold no leaves:read_all, and still
                  needs to know who they are approving for. */}
              {!isOwner && (
                <div data-testid="leave-detail-requester" className="flex items-center space-x-3">
                  <div className="h-10 w-10 shrink-0 bg-primary-100 rounded-full flex items-center justify-center">
                    <User className="h-5 w-5 text-primary-600" />
                  </div>
                  <div className="min-w-0">
                    {canOpenRequester ? (
                      <Link
                        to={`/employees/${request.employee_id}`}
                        data-testid="leave-detail-requester-link"
                        className="text-sm font-medium text-primary-600 hover:text-primary-800"
                      >
                        {requesterName}
                      </Link>
                    ) : (
                      <p className="text-sm font-medium text-gray-900">{requesterName}</p>
                    )}
                    {requesterRole && (
                      <p className="text-xs text-gray-500">{requesterRole}</p>
                    )}
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Leave Type</p>
                  <p className="font-medium text-gray-900">{request.leave_type_name}</p>
                </div>
                <div>
                  <p className="text-gray-500">Business Days</p>
                  <p className="font-medium text-gray-900">{request.business_days}</p>
                </div>
                <div>
                  <p className="text-gray-500">Start Date</p>
                  <p className="font-medium text-gray-900">{formatDateOnly(request.start_date)}</p>
                </div>
                <div>
                  <p className="text-gray-500">End Date</p>
                  <p className="font-medium text-gray-900">{formatDateOnly(request.end_date)}</p>
                </div>
                <div className={wasEdited ? '' : 'col-span-2'}>
                  <p className="text-gray-500">Submitted</p>
                  <p className="font-medium text-gray-900">{new Date(request.created_at).toLocaleString()}</p>
                </div>
                {wasEdited && (
                  <div>
                    <p className="text-gray-500">Edited</p>
                    <p data-testid="leave-detail-edited" className="font-medium text-gray-900">Amended after submission</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Reason */}
          <div className="bg-white rounded-lg shadow-soft border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-base font-semibold text-gray-900 flex items-center">
                <FileText className="h-4 w-4 mr-2 text-primary-600" />
                Reason
              </h2>
            </div>
            <div className="px-6 py-4">
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{request.reason}</p>
            </div>
          </div>

          {/* Approval chain (only rendered when the type configures one) */}
          <LeaveApprovalTrail approval={request.approval} requestStatus={request.status} />

          {/* Review Info (if reviewed) */}
          {request.reviewed_at && (
            <div className="bg-white rounded-lg shadow-soft border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-base font-semibold text-gray-900 flex items-center">
                  <MessageSquare className="h-4 w-4 mr-2 text-primary-600" />
                  Review Decision
                </h2>
              </div>
              <div className="px-6 py-4 space-y-3 text-sm">
                <div>
                  <p className="text-gray-500">Reviewed</p>
                  <p className="font-medium text-gray-900">{new Date(request.reviewed_at).toLocaleString()}</p>
                </div>
                {request.review_comments && (
                  <div>
                    <p className="text-gray-500">Comments</p>
                    <p className="text-gray-800 whitespace-pre-wrap">{request.review_comments}</p>
                  </div>
                )}
                {request.rejection_reason && (
                  <div>
                    <p className="text-gray-500">Rejection Reason</p>
                    <div className="flex items-start gap-2 bg-danger-50 border border-danger-200 rounded-md px-3 py-2 text-danger-800">
                      <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                      <p className="whitespace-pre-wrap">{request.rejection_reason}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* HR Internal Notes (HR-only) */}
          {request.hr_internal_notes !== undefined && (
            <div className="bg-white rounded-lg shadow-soft border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h2 className="text-base font-semibold text-gray-900 flex items-center">
                  <Shield className="h-4 w-4 mr-2 text-gray-500" />
                  Internal Notes
                  <span className="ml-2 text-xs text-gray-400 font-normal">(HR only)</span>
                </h2>
                <button
                  onClick={() => setShowNotes(true)}
                  className="text-xs text-primary-600 hover:text-primary-800 font-medium"
                >
                  Edit
                </button>
              </div>
              <div className="px-6 py-4">
                {request.hr_internal_notes ? (
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{request.hr_internal_notes}</p>
                ) : (
                  <p className="text-sm text-gray-400">No internal notes</p>
                )}
              </div>
            </div>
          )}

          {/* Audit Logs (HR-only) */}
          {auditLogs.length > 0 && (
            <div className="bg-white rounded-lg shadow-soft border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-base font-semibold text-gray-900 flex items-center">
                  <Clock className="h-4 w-4 mr-2 text-primary-600" />
                  Audit Trail
                </h2>
              </div>
              <div className="divide-y divide-gray-100">
                {auditLogs.map(log => (
                  <div key={log.id} className="px-6 py-3 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-900 capitalize">{log.action.replace(/_/g, ' ')}</span>
                      <span className="text-gray-400">{new Date(log.timestamp).toLocaleString()}</span>
                    </div>
                    {log.previous_status && (
                      <p className="text-gray-500 mt-0.5">
                        {formatStatusText(log.previous_status)} → {formatStatusText(log.new_status)}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Actions sidebar */}
        <div className="space-y-4">
          {/* Actions */}
          <div className="bg-white rounded-lg shadow-soft border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-base font-semibold text-gray-900">Actions</h2>
            </div>
            <div className="px-6 py-4 space-y-3">
              {canDecide && (
                <>
                  <button
                    data-testid="leave-detail-approve"
                    onClick={() => setShowApprove(true)}
                    className="w-full inline-flex justify-center items-center px-4 py-2 bg-success-600 text-white text-sm font-medium rounded-md hover:bg-success-700 transition-colors"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Approve
                  </button>
                  <button
                    data-testid="leave-detail-reject"
                    onClick={() => setShowReject(true)}
                    className="w-full inline-flex justify-center items-center px-4 py-2 bg-danger-600 text-white text-sm font-medium rounded-md hover:bg-danger-700 transition-colors"
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Reject
                  </button>
                </>
              )}
              {isOwner && isPending && hasPermission('leaves', 'create_own') && (
                <Link
                  to={`/leaves/${id}/edit`}
                  data-testid="leave-detail-edit"
                  className="w-full inline-flex justify-center items-center px-4 py-2 border border-gray-300 text-gray-700 bg-white text-sm font-medium rounded-md hover:bg-gray-50 transition-colors"
                >
                  <Pencil className="h-4 w-4 mr-2" />
                  Edit Request
                </Link>
              )}
              {/* Undoing an approval is the consequential one: it moves days back onto a
                  balance, so it is tinted rather than sharing the neutral styling of the
                  "Back to List" link directly beneath it. A pending self-cancel stays neutral. */}
              {canCancel && (
                <button
                  data-testid="leave-detail-cancel"
                  onClick={handleCancel}
                  disabled={acting}
                  className={`w-full inline-flex justify-center items-center px-4 py-2 border text-sm font-medium rounded-md bg-white transition-colors disabled:opacity-60 ${
                    isApproved
                      ? 'border-danger-300 text-danger-700 hover:bg-danger-50'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Ban className="h-4 w-4 mr-2" />
                  {isApproved ? 'Cancel Approved Leave' : 'Cancel Request'}
                </button>
              )}
              <Link
                to="/leaves"
                className="w-full inline-flex justify-center items-center px-4 py-2 border border-gray-300 text-gray-700 bg-white text-sm font-medium rounded-md hover:bg-gray-50 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to List
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Approve Modal */}
      {showApprove && (
        <CModal
          onClose={() => setShowApprove(false)}
          labelledBy="approve-leave-title"
          dismissible={!acting}
          closeOnBackdrop={false}
          overlayClassName="bg-black/40"
          className="bg-white rounded-lg shadow-xl w-full max-w-md"
        >
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 id="approve-leave-title" className="text-base font-semibold text-gray-900">Approve Leave Request</h3>
          </div>
          <div className="px-6 py-4 space-y-4">
            <p className="text-sm text-gray-600">
              You are about to approve this leave request. Optionally add comments visible to the employee.
            </p>
            <div>
              <label htmlFor="approve-leave-comments" className="block text-sm font-medium text-gray-700 mb-1">Comments (optional)</label>
              <textarea
                id="approve-leave-comments"
                value={approveComments}
                onChange={e => setApproveComments(e.target.value)}
                rows={3}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 resize-none"
              />
            </div>
          </div>
          <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
            <button
              onClick={() => setShowApprove(false)}
              className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleApprove}
              disabled={acting}
              className="px-4 py-2 bg-success-600 text-white text-sm rounded-md hover:bg-success-700 disabled:opacity-60"
            >
              {acting ? 'Approving…' : 'Approve'}
            </button>
          </div>
        </CModal>
      )}

      {/* Reject Modal */}
      {showReject && (
        <CModal
          onClose={() => setShowReject(false)}
          labelledBy="reject-leave-title"
          dismissible={!acting}
          closeOnBackdrop={false}
          overlayClassName="bg-black/40"
          className="bg-white rounded-lg shadow-xl w-full max-w-md"
          data-testid="leave-detail-confirm-modal"
        >
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 id="reject-leave-title" className="text-base font-semibold text-gray-900">Reject Leave Request</h3>
          </div>
          <div className="px-6 py-4 space-y-4">
            <div>
              <label htmlFor="reject-leave-reason" className="block text-sm font-medium text-gray-700 mb-1">
                Rejection Reason <span className="text-danger-600">*</span>
              </label>
              <textarea
                id="reject-leave-reason"
                data-testid="leave-detail-reject-reason"
                value={rejectReason}
                onChange={e => setRejectReason(e.target.value)}
                rows={3}
                placeholder="Required — this will be visible to the employee"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 resize-none"
              />
            </div>
            <div>
              <label htmlFor="reject-leave-comments" className="block text-sm font-medium text-gray-700 mb-1">Additional Comments (optional)</label>
              <textarea
                id="reject-leave-comments"
                value={rejectComments}
                onChange={e => setRejectComments(e.target.value)}
                rows={2}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 resize-none"
              />
            </div>
          </div>
          <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
            <button
              data-testid="leave-detail-confirm-no"
              onClick={() => setShowReject(false)}
              className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              data-testid="leave-detail-confirm-yes"
              onClick={handleReject}
              disabled={acting}
              className="px-4 py-2 bg-danger-600 text-white text-sm rounded-md hover:bg-danger-700 disabled:opacity-60"
            >
              {acting ? 'Rejecting…' : 'Reject'}
            </button>
          </div>
        </CModal>
      )}

      {/* Internal Notes Modal */}
      {showNotes && (
        <CModal
          onClose={() => { setShowNotes(false); setInternalNotes(request.hr_internal_notes || ''); }}
          labelledBy="hr-notes-title"
          dismissible={!acting}
          closeOnBackdrop={false}
          overlayClassName="bg-black/40"
          className="bg-white rounded-lg shadow-xl w-full max-w-md"
        >
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 id="hr-notes-title" className="text-base font-semibold text-gray-900">HR Internal Notes</h3>
          </div>
          <div className="px-6 py-4">
            {/* No label of its own: the popup's title names it. */}
            <textarea
              aria-labelledby="hr-notes-title"
              value={internalNotes}
              onChange={e => setInternalNotes(e.target.value)}
              rows={5}
              placeholder="Internal notes visible to HR/admins only…"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 resize-none"
            />
          </div>
          <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
            <button
              onClick={() => { setShowNotes(false); setInternalNotes(request.hr_internal_notes || ''); }}
              className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveNotes}
              disabled={acting}
              className="px-4 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700 disabled:opacity-60"
            >
              {acting ? 'Saving…' : 'Save Notes'}
            </button>
          </div>
        </CModal>
      )}
    </div>
  );
};

// One instance per record: see utils/remountOnId.js.
export default remountOnId(LeaveDetail);
